import { useState, useEffect } from 'react'
import { BackofficeLayout } from '../../components/layout/BackofficeLayout'
import { Modal } from '@eshop/shared'
import type { Category } from '../../types'
import { categoriesApi } from '@eshop/shared'

export function CategoriesPage() {
  const [categories, setCategories] = useState<Category[]>([])
  const [searchTerm, setSearchTerm] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<Category | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadCategories()
  }, [])

  const loadCategories = async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await categoriesApi.getAll()
      setCategories(response.results)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar categorías')
      console.error('Error loading categories:', err)
    } finally {
      setLoading(false)
    }
  }

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.slug.toLowerCase().includes(searchTerm.toLowerCase())
  )

  const handleEdit = async (category: Category) => {
    try {
      const fullCategory = await categoriesApi.getById(category.id)
      setEditingCategory(fullCategory)
      setIsModalOpen(true)
    } catch (err) {
      console.error('Error loading category:', err)
      alert('Error al cargar la categoría')
    }
  }

  const handleDelete = async (categoryId: number) => {
    if (confirm('¿Estás seguro de que quieres eliminar esta categoría?')) {
      try {
        await categoriesApi.delete(categoryId)
        await loadCategories()
      } catch (err) {
        console.error('Error deleting category:', err)
        alert('Error al eliminar la categoría')
      }
    }
  }

  const handleCreateNew = () => {
    setEditingCategory(null)
    setIsModalOpen(true)
  }

  const getParentName = (parentId?: number | null) => {
    if (!parentId) return 'Sin categoría padre'
    const parent = categories.find(c => c.id === parentId)
    return parent ? parent.name : '-'
  }

  if (loading) {
    return (
      <BackofficeLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600">Cargando categorías...</p>
        </div>
      </BackofficeLayout>
    )
  }

  if (error) {
    return (
      <BackofficeLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error}</p>
          <button
            onClick={loadCategories}
            className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
          >
            Reintentar
          </button>
        </div>
      </BackofficeLayout>
    )
  }

  return (
    <BackofficeLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Categorías</h1>
            <p className="text-gray-600 mt-1">Organiza tus productos en categorías</p>
          </div>
          <button
            onClick={handleCreateNew}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
          >
            + Nueva Categoría
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Total Categorías</p>
            <p className="text-2xl font-bold text-gray-900">{categories.length}</p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Categorías Principales</p>
            <p className="text-2xl font-bold text-blue-600">
              {categories.filter(c => !c.parent_id).length}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow p-4">
            <p className="text-sm text-gray-600">Subcategorías</p>
            <p className="text-2xl font-bold text-purple-600">
              {categories.filter(c => c.parent_id).length}
            </p>
          </div>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow p-4">
          <input
            type="text"
            placeholder="Buscar por nombre o slug..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        </div>

        {/* Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCategories.length === 0 ? (
            <div className="col-span-full bg-white rounded-lg shadow p-12 text-center">
              <p className="text-gray-500">No se encontraron categorías</p>
            </div>
          ) : (
            filteredCategories.map((category) => (
              <div
                key={category.id}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {category.name}
                      </h3>
                      <p className="text-sm text-gray-500 mb-2">/{category.slug}</p>
                      {category.parent_id && (
                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          Subcategoría de: {getParentName(category.parent_id)}
                        </span>
                      )}
                    </div>
                    {category.image_url && (
                      <img
                        src={category.image_url}
                        alt={category.name}
                        className="w-16 h-16 rounded-lg object-cover ml-4"
                      />
                    )}
                  </div>

                  {category.description && (
                    <p className="text-sm text-gray-600 mb-4 line-clamp-2">
                      {category.description}
                    </p>
                  )}

                  <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                    <span className="text-xs text-gray-500">
                      Creada: {new Date(category.created_at).toLocaleDateString('es-ES')}
                    </span>
                  </div>

                  <div className="flex gap-2 mt-4">
                    <button
                      onClick={() => handleEdit(category)}
                      className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(category.id)}
                      className="flex-1 px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                    >
                      Eliminar
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Summary */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            Mostrando {filteredCategories.length} de {categories.length} categorías
          </p>
        </div>
      </div>

      {/* Category Modal */}
      {isModalOpen && (
        <CategoryModal
          category={editingCategory}
          categories={categories}
          onClose={() => setIsModalOpen(false)}
          onSave={async (category) => {
            try {
              if (editingCategory) {
                await categoriesApi.update(editingCategory.id, category)
              } else {
                await categoriesApi.create(category)
              }
              await loadCategories()
              setIsModalOpen(false)
            } catch (err: any) {
              console.error('Error saving category:', err)
              alert(err.response?.data?.detail || 'Error al guardar la categoría')
            }
          }}
        />
      )}
    </BackofficeLayout>
  )
}

interface CategoryModalProps {
  category: Category | null
  categories: Category[]
  onClose: () => void
  onSave: (category: Category) => void
}

function CategoryModal({ category, categories, onClose, onSave }: CategoryModalProps) {
  const [formData, setFormData] = useState<Partial<Category>>(
    category || {
      name: '',
      slug: '',
      description: '',
      parent_id: null,
    }
  )

  const handleNameChange = (name: string) => {
    const slug = name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')

    setFormData({ ...formData, name, slug })
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const now = new Date().toISOString()
    onSave({
      ...formData,
      created_at: category?.created_at || now,
      updated_at: now,
    } as Category)
  }

  const availableParents = categories.filter(c => c.id !== category?.id)

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={category ? 'Editar Categoría' : 'Nueva Categoría'}
      maxWidth="lg"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Nombre de la categoría
          </label>
          <input
            type="text"
            required
            value={formData.name}
            onChange={(e) => handleNameChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Ej: Electrónica"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Slug (URL amigable)
          </label>
          <input
            type="text"
            required
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="electronica"
          />
          <p className="mt-1 text-xs text-gray-500">
            Se genera automáticamente desde el nombre
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Descripción
          </label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            rows={3}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Describe esta categoría..."
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Categoría Padre
          </label>
          <select
            value={formData.parent_id || ''}
            onChange={(e) => setFormData({
              ...formData,
              parent_id: e.target.value ? parseInt(e.target.value) : null
            })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Sin categoría padre (categoría principal)</option>
            {availableParents.filter(c => !c.parent_id).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Selecciona una categoría padre para crear una subcategoría
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            URL de Imagen (opcional)
          </label>
          <input
            type="url"
            value={formData.image_url || ''}
            onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="https://ejemplo.com/imagen.jpg"
          />
        </div>

        <div className="flex justify-end gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            {category ? 'Actualizar' : 'Crear'} Categoría
          </button>
        </div>
      </form>
    </Modal>
  )
}
